import axios from 'axios';
import { getImageUrl, getImageUrlCandidates, getBestTeamLogo, parseMisc } from '../utils/imageUtils';

export { getImageUrl, getImageUrlCandidates, getBestTeamLogo, parseMisc };

const API_URL = process.env.REACT_APP_API_URL || 'https://apimon.mf-league.com/api';

// Extract base URL without /api for images
const BASE_URL = API_URL.replace(/\/api\/?$/, '');

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/signin', { username, password });
    const { accessToken, ...user } = response.data;
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(user));
    return response.data;
  },

  register: async (username, email, password) => {
    const response = await api.post('/auth/signup', { username, email, password });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    return JSON.parse(localStorage.getItem('user') || 'null');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Change current user's password
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/user/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }
};

// User management API (Admin only)
export const userAPI = {
  // Get all users
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  // Get user by ID
  getById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Update user
  update: async (id, data) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  // Delete user
  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  // Admin: Change user password
  changeUserPassword: async (userId, newPassword) => {
    const response = await api.put('/admin/change-user-password', {
      userId,
      newPassword
    });
    return response.data;
  },

  // Admin: Update user roles
  updateUserRoles: async (userId, roles) => {
    const response = await api.put('/admin/update-user-roles', {
      userId,
      roles
    });
    return response.data;
  }
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`)
};

// Seasons API
export const seasonsAPI = {
  getAll: () => api.get('/seasons'),
  getById: (id) => api.get(`/seasons/${id}`),
  create: (data) => api.post('/seasons', data),
  update: (id, data) => api.put(`/seasons/${id}`, data),
  delete: (id) => api.delete(`/seasons/${id}`)
};

// Positions API
export const positionsAPI = {
  getAll: () => api.get('/positions'),
  getById: (id) => api.get(`/positions/${id}`),
  create: (data) => api.post('/positions', data),
  update: (id, data) => api.put(`/positions/${id}`, data),
  delete: (id) => api.delete(`/positions/${id}`)
};

// Referees API
export const refereesAPI = {
  getAll: () => api.get('/referees'),
  getById: (id) => api.get(`/referees/${id}`),
  create: (data) => api.post('/referees', data),
  update: (id, data) => api.put(`/referees/${id}`, data),
  delete: (id) => api.delete(`/referees/${id}`)
};

// Stadia API
export const stadiaAPI = {
  getAll: () => api.get('/stadia/all'),
  getById: (id) => api.get(`/stadia/${id}`),
  create: (data) => api.post('/stadia', data),
  update: (id, data) => api.put(`/stadia/${id}`, data),
  delete: (id) => api.delete(`/stadia/${id}`)
};

// Teams API
export const teamsAPI = {
  getAll: () => api.get('/teams/all'),
  getByProject: (pid) => api.get(`/teams/project/${pid}`),
  getUnassigned: () => api.get('/teams/unassigned'),
  getById: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  uploadFiles: (formData) => {
    return api.post('/teams/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// Teamtools API
export const teamtoolsAPI = {
  getAll: () => api.get('/teamtools'),
  getByProject: (pid) => api.get(`/teamtools?pid=${pid}`),
  create: (data) => api.post('/teamtools', data),
  update: (id, data) => api.put(`/teamtools/${id}`, data),
  delete: (id) => api.delete(`/teamtools/${id}`),
  assignTeamToProject: (data) => api.post('/teamtools/assign', data),
  removeTeamFromProject: (pid, tid) => api.delete(`/teamtools/remove/${pid}/${tid}`)
};

// Players API
export const playersAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/player${queryString ? '?' + queryString : ''}`);
  },
  getById: (id) => api.get(`/player/${id}`),
  create: (data) => api.post('/player', data),
  update: (id, data) => api.put(`/player/${id}`, data),
  delete: (id) => api.delete(`/player/${id}`),
  uploadFiles: (formData) => {
    return api.post('/player/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// Playertools API
export const playertoolsAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/playertools${queryString ? '?' + queryString : ''}`);
  },
  getById: (id) => api.get(`/playertools/${id}`),
  getByPlayer: (playerId) => api.get(`/playertools/player/${playerId}`),
  create: (data) => api.post('/playertools', data),
  update: (id, data) => api.put(`/playertools/${id}`, data),
  delete: (id) => api.delete(`/playertools/${id}`)
};

// Rounds API
export const roundsAPI = {
  getAll: () => api.get('/rounds'),
  getByProject: (pid) => api.get(`/rounds?pid=${pid}`),
  getById: (id) => api.get(`/rounds/${id}`),
  create: (data) => api.post('/rounds', data),
  update: (id, data) => api.put(`/rounds/${id}`, data),
  delete: (id) => api.delete(`/rounds/${id}`)
};

// Games API
export const gamesAPI = {
  getAll: () => api.get('/games'),
  getByRound: (rid) => api.get(`/games?rid=${rid}`),
  getById: (id) => api.get(`/games/${id}`),
  create: (data) => api.post('/games', data),
  update: (id, data) => api.put(`/games/${id}`, data),
  delete: (id) => api.delete(`/games/${id}`)
};

// Events API
export const eventsAPI = {
  getAll: () => api.get('/events'),
  getByGame: (gid) => api.get(`/events/game/${gid}`),
  getById: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`)
};

// Gameplayer (Lineup/Roster) API
export const gameplayerAPI = {
  getByGame: (gid) => api.get(`/gameplayer/gid/${gid}`),
  getById: (id) => api.get(`/gameplayer/${id}`),
  create: (data) => api.post('/gameplayer', data),
  update: (id, data) => api.put(`/gameplayer/${id}`, data),
  delete: (id) => api.delete(`/gameplayer/${id}`)
};

// Ticker API
export const tickerAPI = {
  getByGame: (gameId) => api.get(`/ticker/game/${gameId}`),
  getByGameLegacy: (gid) => api.get(`/ticker/${gid}`),
  create: (data) => api.post('/ticker', data),
  update: (id, data) => api.put(`/ticker/${id}`, data),
  delete: (id) => api.delete(`/ticker/${id}`),
  setReportFlag: (id, reportFlags) => api.put(`/ticker/${id}/report-flag`, { reportFlags }),
  publish: (id) => api.put(`/ticker/${id}/publish`)
};

// Match Meta API
export const matchMetaAPI = {
  get: (gameId) => api.get(`/match-meta/${gameId}`),
  update: (gameId, data) => api.put(`/match-meta/${gameId}`, data),
  deriveTags: (gameId) => api.post(`/match-meta/${gameId}/derive-tags`),
  tickerStart: (gameId) => api.put(`/match-meta/${gameId}/ticker-start`),
  tickerStop: (gameId) => api.put(`/match-meta/${gameId}/ticker-stop`),
};

// AI API
export const aiAPI = {
  improveText:     (text)   => api.post('/ai/ticker/improve',    { text }),
  spellcheck:      (text)   => api.post('/ai/ticker/spellcheck', { text }),
  keywords:        (text)   => api.post('/ai/ticker/keywords',   { text }),
  generateReport:  (gameId, opts = {}) => api.post(`/ai/report/${gameId}`, opts),
  getReportData:   (gameId) => api.get(`/ai/report-data/${gameId}`),
};

// Socket base URL — use dedicated env var if set, otherwise derive from API_URL
// In development: REACT_APP_SOCKET_URL=http://localhost:8080
// In production:  REACT_APP_SOCKET_URL=https://apimon.mf-league.com
export const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || BASE_URL;

// Event Types API
export const eventTypesAPI = {
  getAll: () => api.get('/eventtypes'),
  getById: (id) => api.get(`/eventtypes/${id}`),
  create: (data) => api.post('/eventtypes', data),
  update: (id, data) => api.put(`/eventtypes/${id}`, data),
  delete: (id) => api.delete(`/eventtypes/${id}`)
};

// Users API
export const usersAPI = {
  // User management (Admin only)
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  
  // Password management
  changePassword: (currentPassword, newPassword) => 
    api.put('/user/change-password', { currentPassword, newPassword }),
  
  // Admin functions
  adminChangeUserPassword: (userId, newPassword) => 
    api.put('/admin/change-user-password', { userId, newPassword }),
  
  updateUserRoles: (userId, roles) => 
    api.put('/admin/update-user-roles', { userId, roles }),
};

// App API Configuration
export const appAPI = {
  // Get current app configuration
  getConfig: () => api.get('/appconfig'),
  
  // Save app configuration
  saveConfig: (config) => api.post('/appconfig', config),
  
  // Update specific endpoint configuration
  updateEndpoint: (endpoint, slot, projectId) => 
    api.put('/appconfig/endpoint', { endpoint, slot, projectId }),
  
  // Open endpoint URL in new tab for manual testing
  getEndpointUrl: (endpoint, slot) =>
    `${API_URL.replace('/api', '')}/appapi/${endpoint}/${slot}`
};

export default api;
